import { useState, useEffect } from 'react';
import { Calculator, DollarSign, Bookmark, ArrowRight, Landmark, Calendar, ShieldAlert, Award, FileText, CheckCircle, Trash, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const PAYMENT_METHOD_INFO = {
  efectivo: { label: 'Efectivo', icon: '💵', badgeClass: 'bg-green-100 text-green-800 border-green-200' },
  datafono: { label: 'Datáfono', icon: '💳', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
  trans_keiks: { label: 'Trans Keiks', icon: '🏦', badgeClass: 'bg-purple-100 text-purple-800 border-purple-200' },
  trans_esteba: { label: 'Trans Esteba', icon: '🏦', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  online: { label: 'Online / Pasarela', icon: '🌐', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' },
  rappi: { label: 'Rappi', icon: '🛵', badgeClass: 'bg-orange-100 text-orange-800 border-orange-200' },
  credito: { label: 'Crédito', icon: '📝', badgeClass: 'bg-gray-100 text-gray-800 border-gray-200' },
};

export default function AdminCashRegister() {
  const { user: currentUser } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [activeStore, setActiveStore] = useState(null); // The store currently active for balancing
  const [products, setProducts] = useState([]);

  // Session State
  const [currentSession, setCurrentSession] = useState(null);
  const [requiresSelection, setRequiresSelection] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [sessionsHistory, setSessionsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState('register');

  // Open Form State
  const [openBaseCash, setOpenBaseCash] = useState('50000');
  const [expensesBase, setExpensesBase] = useState('30000');
  const [initialInventory, setInitialInventory] = useState({}); // productId -> { small, medium, large }

  // Close Form State
  const [finalInventory, setFinalInventory] = useState({}); // productId -> { small, medium, large }
  const [production, setProduction] = useState({}); // productId -> { small, medium, large }
  const [currentSales, setCurrentSales] = useState({ calculated_income: 0, cash_income: 0, payments_by_method: {}, order_quantities: {}, orders_count: 0, orders_detail: [] });
  const [cashReported, setCashReported] = useState('');
  const [expensesTotal, setExpensesTotal] = useState('0');

  // Detail Modal State
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStoresAndProducts = async () => {
    try {
      let storesData = [];
      try {
        const adminStoresRes = await axios.get(`${API}/admin/stores`, { headers: getHeaders(), withCredentials: true });
        storesData = adminStoresRes.data;
      } catch {
        const publicStoresRes = await axios.get(`${API}/stores?include_hidden=true`);
        storesData = publicStoresRes.data;
      }
      const productsRes = await axios.get(`${API}/products`);
      setStores(storesData);
      const cashProducts = (productsRes.data || []).filter(p => p.include_in_cash_closing !== false);
      setProducts(cashProducts);
      
      // Initialize inventory maps
      const initialMap = {};
      cashProducts.forEach(p => {
        initialMap[p.id] = { small: 0, medium: 0, large: 0 };
      });
      setInitialInventory(initialMap);
      setFinalInventory(initialMap);
      setProduction(initialMap);
      
      // Set active store if user is assigned to one
      if (currentUser?.store_id) {
        const userStore = storesData.find(s => s.id === currentUser.store_id);
        setActiveStore(userStore);
        setSelectedStoreId(currentUser.store_id);
      } else if (storesData.length > 0) {
        // Super admin default
        setActiveStore(storesData[0]);
        setSelectedStoreId(storesData[0].id);
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar datos base');
    }
  };

  const fetchCurrentSession = async (storeId) => {
    if (!storeId || storeId === 'all') return;
    setLoadingSession(true);
    try {
      const { data } = await axios.get(`${API}/admin/cash/session/current`, {
        params: { store_id: storeId },
        headers: getHeaders(),
        withCredentials: true
      });
      if (data.requires_store_selection) {
        setRequiresSelection(true);
        setCurrentSession(null);
      } else {
        setRequiresSelection(false);
        setCurrentSession(data.session);
        setCurrentSales(data.current_sales || { calculated_income: 0, cash_income: 0, payments_by_method: {}, order_quantities: {}, orders_count: 0, orders_detail: [] });

        if (data.session?.initial_inventory) {
          const initMap = {};
          const finMap = {};
          data.session.initial_inventory.forEach(item => {
            initMap[item.productId] = {
              small: item.small || 0,
              medium: item.medium || 0,
              large: item.large || 0,
            };
            const salesQ = data.current_sales?.order_quantities?.[item.productId] || {};
            finMap[item.productId] = {
              small: Math.max(0, (item.small || 0) - (salesQ.small || 0)),
              medium: Math.max(0, (item.medium || 0) - (salesQ.medium || 0)),
              large: Math.max(0, (item.large || 0) - (salesQ.large || 0)),
            };
          });
          setInitialInventory(initMap);
          setFinalInventory(finMap);
        }
      }
    } catch {
      toast.error('Error al verificar sesión de caja');
    } finally {
      setLoadingSession(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/admin/cash/sessions`, {
        params: { store_id: selectedStoreId },
        headers: getHeaders(),
        withCredentials: true
      });
      setSessionsHistory(data);
    } catch {
      toast.error('Error al cargar historial de cajas');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchStoresAndProducts();
  }, [currentUser]);

  useEffect(() => {
    if (selectedStoreId && selectedStoreId !== 'all') {
      const store = stores.find(s => s.id === selectedStoreId);
      setActiveStore(store);
      fetchCurrentSession(selectedStoreId);
    }
  }, [selectedStoreId, stores]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, selectedStoreId]);

  const handleOpenRegister = async (e) => {
    e.preventDefault();
    if (!activeStore) return;

    // Convert inventory map to list
    const initialList = Object.entries(initialInventory).map(([prodId, qtys]) => {
      const prod = products.find(p => p.id === prodId);
      return {
        productId: prodId,
        productName: prod ? prod.name : 'Unknown',
        small: parseInt(qtys.small || 0),
        medium: parseInt(qtys.medium || 0),
        large: parseInt(qtys.large || 0)
      };
    });

    const payload = {
      store_id: activeStore.id,
      store_name: activeStore.name,
      opening_base_cash: parseFloat(openBaseCash || 0),
      expenses_base: parseFloat(expensesBase || 0),
      initial_inventory: initialList
    };

    try {
      const { data } = await axios.post(`${API}/admin/cash/session/open`, payload, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('Caja abierta correctamente');
      setCurrentSession(data);
      await fetchCurrentSession(activeStore.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al abrir caja');
    }
  };

  const handleCloseRegister = async (e) => {
    e.preventDefault();
    if (!currentSession) return;

    if (!cashReported) {
      toast.error('Debes ingresar el efectivo reportado al cierre');
      return;
    }

    const finalList = Object.entries(finalInventory).map(([prodId, qtys]) => {
      const prod = products.find(p => p.id === prodId);
      const initS = initialInventory[prodId]?.small ?? 0;
      const initM = initialInventory[prodId]?.medium ?? 0;
      const initL = initialInventory[prodId]?.large ?? 0;

      const prodS = parseInt(production[prodId]?.small || 0);
      const prodM = parseInt(production[prodId]?.medium || 0);
      const prodL = parseInt(production[prodId]?.large || 0);

      const saleS = currentSales.order_quantities?.[prodId]?.small ?? 0;
      const saleM = currentSales.order_quantities?.[prodId]?.medium ?? 0;
      const saleL = currentSales.order_quantities?.[prodId]?.large ?? 0;

      const calcS = Math.max(0, initS + prodS - saleS);
      const calcM = Math.max(0, initM + prodM - saleM);
      const calcL = Math.max(0, initL + prodL - saleL);

      return {
        productId: prodId,
        productName: prod ? prod.name : 'Unknown',
        small: qtys.small !== undefined && qtys.small !== '' ? parseInt(qtys.small) : calcS,
        medium: qtys.medium !== undefined && qtys.medium !== '' ? parseInt(qtys.medium) : calcM,
        large: qtys.large !== undefined && qtys.large !== '' ? parseInt(qtys.large) : calcL,
      };
    });

    const prodList = Object.entries(production).map(([prodId, qtys]) => {
      const prod = products.find(p => p.id === prodId);
      return {
        productId: prodId,
        productName: prod ? prod.name : 'Unknown',
        small: parseInt(qtys.small || 0),
        medium: parseInt(qtys.medium || 0),
        large: parseInt(qtys.large || 0)
      };
    });

    const payload = {
      final_inventory: finalList,
      production: prodList,
      cash_reported: parseFloat(cashReported),
      expenses_total: parseFloat(expensesTotal || 0)
    };

    try {
      const { data } = await axios.post(`${API}/admin/cash/session/close/${currentSession.id}`, payload, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success(`Caja cerrada. Diferencia: ${formatPrice(data.difference)}`);
      setCurrentSession(null);
      fetchCurrentSession(selectedStoreId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  const handleProductionChange = (prodId, size, value) => {
    const val = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    const numVal = parseInt(val || 0);
    setProduction(prev => ({
      ...prev,
      [prodId]: {
        ...prev[prodId],
        [size]: val
      }
    }));

    // Recalculate and update finalInventory
    const init = initialInventory[prodId]?.[size] || 0;
    const sales = currentSales.order_quantities?.[prodId]?.[size] || 0;
    const newFinal = Math.max(0, init + numVal - sales);
    setFinalInventory(prev => ({
      ...prev,
      [prodId]: {
        ...prev[prodId],
        [size]: newFinal
      }
    }));
  };

  const syncAllCalculatedToFinal = () => {
    const synced = {};
    products.forEach(p => {
      const initS = initialInventory[p.id]?.small ?? 0;
      const initM = initialInventory[p.id]?.medium ?? 0;
      const initL = initialInventory[p.id]?.large ?? 0;

      const prodS = parseInt(production[p.id]?.small || 0);
      const prodM = parseInt(production[p.id]?.medium || 0);
      const prodL = parseInt(production[p.id]?.large || 0);

      const saleS = currentSales.order_quantities?.[p.id]?.small ?? 0;
      const saleM = currentSales.order_quantities?.[p.id]?.medium ?? 0;
      const saleL = currentSales.order_quantities?.[p.id]?.large ?? 0;

      synced[p.id] = {
        small: Math.max(0, initS + prodS - saleS),
        medium: Math.max(0, initM + prodM - saleM),
        large: Math.max(0, initL + prodL - saleL),
      };
    });
    setFinalInventory(synced);
    toast.success('Inventario físico sincronizado con el cálculo esperado');
  };

  const handleQtyChange = (map, setter, prodId, size, value) => {
    setter(prev => ({
      ...prev,
      [prodId]: {
        ...prev[prodId],
        [size]: value === '' ? '' : Math.max(0, parseInt(value) || 0)
      }
    }));
  };

  const openHistoryDetail = (session) => {
    setSelectedHistorySession(session);
    setIsDetailOpen(true);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta sesión de cuadre de caja? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/admin/cash/sessions/${sessionId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('Sesión de caja eliminada');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar sesión de caja');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-cash-register-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Cuadre de Caja Diario</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">
            Realiza la apertura y cierre de caja diario, y lleva el control del stock físico de tortas.
          </p>
        </div>

        {/* Store Selector (Visible to Super Admin or showing read-only to assigned store) */}
        {!currentUser?.store_id ? (
          <div className="flex items-center gap-2">
            <span className="font-body text-xs text-[#75664d]">Gestionar Sede:</span>
            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
              <SelectTrigger className="w-56 rounded-xl border-[#e5dfd3] bg-white">
                <SelectValue placeholder="Seleccionar sede" />
              </SelectTrigger>
              <SelectContent>
                {stores.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <Badge className="bg-[#a0d5ba]/20 text-[#46371c] font-body text-sm py-1.5 px-3 border border-[#a0d5ba]">
            <Landmark className="w-4 h-4 mr-1.5" /> Sede: {activeStore?.name}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#f4f1ea] border border-[#e5dfd3] p-1 rounded-xl mb-4">
          <TabsTrigger value="register" className="rounded-lg px-4 py-2 font-body text-sm flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#46371c] data-[state=active]:shadow-sm">
            <Calculator className="w-4 h-4" /> Cuadre de Caja
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg px-4 py-2 font-body text-sm flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#46371c] data-[state=active]:shadow-sm">
            <FileText className="w-4 h-4" /> Historial de Sesiones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="register">
          {loadingSession ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : currentSession ? (
            /* ==================== REGISTER IS OPEN ==================== */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <form onSubmit={handleCloseRegister}>
                  <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl space-y-6">
                    <div className="flex items-center justify-between border-b border-[#e5dfd3]/60 pb-4">
                      <h3 className="font-heading text-lg font-bold text-[#46371c]">Formulario de Cierre de Caja</h3>
                      <Badge className="bg-green-100 text-green-800">Caja Abierta</Badge>
                    </div>

                    {/* Inventory Table for closing */}
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div>
                          <h4 className="font-heading text-sm font-semibold text-[#46371c]">Inventario Final y Producción</h4>
                          <p className="font-body text-xs text-[#75664d]">
                            El sistema calcula automáticamente: <strong>Inv. Final = Inicial + Producción - Ventas</strong>.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              fetchCurrentSession(selectedStoreId || activeStore?.id);
                              toast.success('Ventas e inventario calculados actualizados');
                            }}
                            className="h-8 text-xs font-body rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar Ventas
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={syncAllCalculatedToFinal}
                            className="h-8 text-xs font-body rounded-lg border-[#a0d5ba] text-[#347a54] hover:bg-[#eef6f2]"
                          >
                            Usar Inventario Calculado
                          </Button>
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto border border-[#e5dfd3]/60 rounded-xl">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-[#f4f1ea]">
                              <TableHead className="font-body text-xs font-semibold text-[#46371c] min-w-[130px]">Torta</TableHead>
                              <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>1. Inv. Inicial</TableHead>
                              <TableHead className="font-body text-xs font-semibold text-green-800 text-center" colSpan={3}>2. (+) Producción</TableHead>
                              <TableHead className="font-body text-xs font-semibold text-blue-800 text-center" colSpan={3}>3. (-) Ventas</TableHead>
                              <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>4. (=) Inv. Calculado</TableHead>
                              <TableHead className="font-body text-xs font-semibold text-[#e05275] text-center" colSpan={3}>5. Inv. Físico (Cierre)</TableHead>
                            </TableRow>
                            <TableRow className="bg-[#f4f1ea]/40 border-t border-[#e5dfd3]/40">
                              <TableHead></TableHead>
                              {/* Initial */}
                              <TableHead className="text-center font-body text-[10px] text-[#75664d]">P</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#75664d]">M</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#75664d]">G</TableHead>
                              {/* Production */}
                              <TableHead className="text-center font-body text-[10px] text-green-700">P</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-green-700">M</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-green-700">G</TableHead>
                              {/* Sales */}
                              <TableHead className="text-center font-body text-[10px] text-blue-700">P</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-blue-700">M</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-blue-700">G</TableHead>
                              {/* Calculated */}
                              <TableHead className="text-center font-body text-[10px] text-[#46371c] font-bold">P</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#46371c] font-bold">M</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#46371c] font-bold">G</TableHead>
                              {/* Physical */}
                              <TableHead className="text-center font-body text-[10px] text-[#e05275] font-bold">P</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#e05275] font-bold">M</TableHead>
                              <TableHead className="text-center font-body text-[10px] text-[#e05275] font-bold">G</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {products.map(p => {
                              const initS = initialInventory[p.id]?.small ?? 0;
                              const initM = initialInventory[p.id]?.medium ?? 0;
                              const initL = initialInventory[p.id]?.large ?? 0;

                              const prodS = parseInt(production[p.id]?.small || 0);
                              const prodM = parseInt(production[p.id]?.medium || 0);
                              const prodL = parseInt(production[p.id]?.large || 0);

                              const saleS = currentSales.order_quantities?.[p.id]?.small ?? 0;
                              const saleM = currentSales.order_quantities?.[p.id]?.medium ?? 0;
                              const saleL = currentSales.order_quantities?.[p.id]?.large ?? 0;

                              const calcS = Math.max(0, initS + prodS - saleS);
                              const calcM = Math.max(0, initM + prodM - saleM);
                              const calcL = Math.max(0, initL + prodL - saleL);

                              const finS = finalInventory[p.id]?.small !== undefined && finalInventory[p.id]?.small !== '' ? finalInventory[p.id]?.small : calcS;
                              const finM = finalInventory[p.id]?.medium !== undefined && finalInventory[p.id]?.medium !== '' ? finalInventory[p.id]?.medium : calcM;
                              const finL = finalInventory[p.id]?.large !== undefined && finalInventory[p.id]?.large !== '' ? finalInventory[p.id]?.large : calcL;

                              return (
                                <TableRow key={p.id}>
                                  <TableCell className="font-body text-xs font-semibold text-[#46371c]">{p.name}</TableCell>
                                  {/* Initial */}
                                  <TableCell className="text-center font-body text-xs text-[#75664d] bg-[#fcfbf9]">{initS}</TableCell>
                                  <TableCell className="text-center font-body text-xs text-[#75664d] bg-[#fcfbf9]">{initM}</TableCell>
                                  <TableCell className="text-center font-body text-xs text-[#75664d] bg-[#fcfbf9]">{initL}</TableCell>
                                  {/* Production */}
                                  <TableCell className="bg-green-50/40">
                                    <input type="number" min={0} value={production[p.id]?.small ?? 0} onChange={e => handleProductionChange(p.id, 'small', e.target.value)} className="w-10 h-7 text-center border border-green-200 rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba] bg-white" />
                                  </TableCell>
                                  <TableCell className="bg-green-50/40">
                                    <input type="number" min={0} value={production[p.id]?.medium ?? 0} onChange={e => handleProductionChange(p.id, 'medium', e.target.value)} className="w-10 h-7 text-center border border-green-200 rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba] bg-white" />
                                  </TableCell>
                                  <TableCell className="bg-green-50/40">
                                    <input type="number" min={0} value={production[p.id]?.large ?? 0} onChange={e => handleProductionChange(p.id, 'large', e.target.value)} className="w-10 h-7 text-center border border-green-200 rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba] bg-white" />
                                  </TableCell>
                                  {/* Sales */}
                                  <TableCell className="text-center font-body text-xs font-semibold text-blue-700 bg-blue-50/30">{saleS}</TableCell>
                                  <TableCell className="text-center font-body text-xs font-semibold text-blue-700 bg-blue-50/30">{saleM}</TableCell>
                                  <TableCell className="text-center font-body text-xs font-semibold text-blue-700 bg-blue-50/30">{saleL}</TableCell>
                                  {/* Calculated Final */}
                                  <TableCell className="text-center font-body text-xs font-bold text-[#46371c] bg-[#eef6f2]">{calcS}</TableCell>
                                  <TableCell className="text-center font-body text-xs font-bold text-[#46371c] bg-[#eef6f2]">{calcM}</TableCell>
                                  <TableCell className="text-center font-body text-xs font-bold text-[#46371c] bg-[#eef6f2]">{calcL}</TableCell>
                                  {/* Physical Final */}
                                  <TableCell className="bg-amber-50/30">
                                    <input type="number" min={0} value={finS} onChange={e => handleQtyChange(finalInventory, setFinalInventory, p.id, 'small', e.target.value)} className={`w-10 h-7 text-center border rounded-lg font-body text-xs focus:outline-none bg-white ${parseInt(finS) !== calcS ? 'border-amber-500 font-bold text-amber-700 ring-1 ring-amber-400' : 'border-[#e5dfd3]'}`} />
                                  </TableCell>
                                  <TableCell className="bg-amber-50/30">
                                    <input type="number" min={0} value={finM} onChange={e => handleQtyChange(finalInventory, setFinalInventory, p.id, 'medium', e.target.value)} className={`w-10 h-7 text-center border rounded-lg font-body text-xs focus:outline-none bg-white ${parseInt(finM) !== calcM ? 'border-amber-500 font-bold text-amber-700' : 'border-[#e5dfd3]'}`} />
                                  </TableCell>
                                  <TableCell className="bg-amber-50/30">
                                    <input type="number" min={0} value={finL} onChange={e => handleQtyChange(finalInventory, setFinalInventory, p.id, 'large', e.target.value)} className={`w-10 h-7 text-center border rounded-lg font-body text-xs focus:outline-none bg-white ${parseInt(finL) !== calcL ? 'border-amber-500 font-bold text-amber-700' : 'border-[#e5dfd3]'}`} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-[#e5dfd3]/60 pt-4">
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Gastos Totales de Caja *</label>
                        <input
                          type="number"
                          value={expensesTotal}
                          onChange={e => setExpensesTotal(e.target.value)}
                          placeholder="0"
                          className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Efectivo Reportado en Caja *</label>
                        <input
                          type="number"
                          value={cashReported}
                          onChange={e => setCashReported(e.target.value)}
                          required
                          placeholder="Ingresa el efectivo actual"
                          className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl h-11">
                      Cerrar Caja
                    </Button>
                  </Card>
                </form>
              </div>

              {/* Side Dashboard for Stats */}
              <div>
                <Card className="p-6 border border-[#e5dfd3] bg-[#eef6f2] rounded-2xl space-y-6 sticky top-24">
                  <div>
                    <h3 className="font-heading text-base font-bold text-[#46371c] flex items-center gap-1.5"><Landmark className="w-4 h-4 text-[#a0d5ba]" />{currentSession.store_name}</h3>
                    <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mt-1"><Calendar className="w-3.5 h-3.5" />Apertura: {new Date(currentSession.created_at).toLocaleString('es-CO')}</p>
                  </div>

                  <div className="border-t border-[#e5dfd3] pt-4 space-y-4 font-body text-sm text-[#75664d]">
                    <div className="flex justify-between">
                      <span>Base de Caja (Efectivo):</span>
                      <span className="font-semibold text-[#46371c]">{formatPrice(currentSession.opening_base_cash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Base para Gastos:</span>
                      <span className="font-semibold text-[#46371c]">{formatPrice(currentSession.expenses_base)}</span>
                    </div>
                    
                    {/* Ventas Totales */}
                    <div className="flex justify-between text-[#46371c] font-semibold border-t border-[#e5dfd3]/60 pt-2">
                      <span>Total Ventas ({currentSales.orders_count || 0} pedidos):</span>
                      <span className="font-bold text-base">{formatPrice(currentSales.calculated_income || 0)}</span>
                    </div>

                    {/* Resumen discriminado de todas las formas de pago */}
                    <div className="bg-white/95 p-3.5 rounded-2xl border border-[#e5dfd3] shadow-sm space-y-2">
                      <div className="flex justify-between items-center border-b border-[#e5dfd3]/60 pb-1.5">
                        <span className="font-heading font-bold text-xs text-[#46371c] flex items-center gap-1">
                          Discriminación por Forma de Pago:
                        </span>
                        <Badge className="bg-[#a0d5ba]/20 text-[#46371c] border border-[#a0d5ba] text-[10px] font-bold">
                          {currentSales.orders_count || 0} {currentSales.orders_count === 1 ? 'pedido' : 'pedidos'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1.5 pt-1">
                        {Object.keys(PAYMENT_METHOD_INFO).map((methodKey) => {
                          const info = PAYMENT_METHOD_INFO[methodKey];
                          const amt = currentSales.payments_by_method?.[methodKey] || 0;
                          return (
                            <div key={methodKey} className={`flex justify-between items-center px-2 py-1 rounded-lg text-xs transition-colors ${amt > 0 ? (methodKey === 'efectivo' ? 'bg-green-50 border border-green-200' : 'bg-[#fcfbf9] border border-[#e5dfd3]/60') : 'opacity-60'}`}>
                              <span className="flex items-center gap-1.5 font-medium text-[#46371c]">
                                <span>{info.icon}</span> {info.label}
                                {methodKey === 'efectivo' && <span className="text-[10px] text-green-700 font-bold">(Caja)</span>}
                              </span>
                              <span className={`font-semibold ${amt > 0 ? (methodKey === 'efectivo' ? 'text-green-800 font-bold' : 'text-[#46371c]') : 'text-[#75664d]'}`}>
                                {formatPrice(amt)}
                              </span>
                            </div>
                          );
                        })}

                        {/* Custom / unknown payment methods if any */}
                        {Object.entries(currentSales.payments_by_method || {})
                          .filter(([k]) => !PAYMENT_METHOD_INFO[k])
                          .map(([k, amt]) => (
                            <div key={k} className="flex justify-between items-center px-2 py-1 rounded-lg text-xs bg-[#fcfbf9] border border-[#e5dfd3]/60">
                              <span className="flex items-center gap-1.5 font-medium text-[#46371c]">
                                <span>💰</span> {k.toUpperCase()}
                              </span>
                              <span className="font-semibold text-[#46371c]">{formatPrice(amt)}</span>
                            </div>
                          ))}
                      </div>

                      {/* Subtotal non-cash summary */}
                      <div className="border-t border-[#e5dfd3]/60 pt-1.5 flex justify-between text-[11px] text-[#75664d]">
                        <span>Bancos / Pasarelas / Otros:</span>
                        <span className="font-medium">{formatPrice((currentSales.calculated_income || 0) - (currentSales.cash_income || 0))}</span>
                      </div>
                    </div>

                    <div className="flex justify-between text-red-600">
                      <span>Gastos de Caja:</span>
                      <span className="font-semibold">{formatPrice(parseFloat(expensesTotal || 0))}</span>
                    </div>

                    {/* Conciliación estricta de efectivo físico en registradora */}
                    <div className="border-t border-[#e5dfd3] pt-3 space-y-1">
                      <div className="flex justify-between font-bold text-[#46371c] text-sm">
                        <span>Efectivo Físico Esperado:</span>
                        <span className="text-base text-green-800 font-bold">
                          {formatPrice(currentSession.opening_base_cash + (currentSales.cash_income || 0) - parseFloat(expensesTotal || 0))}
                        </span>
                      </div>
                      <p className="text-[10px] text-[#75664d] leading-tight">
                        Cálculo: Base ({formatPrice(currentSession.opening_base_cash)}) + Ventas Efectivo ({formatPrice(currentSales.cash_income || 0)}) - Gastos ({formatPrice(parseFloat(expensesTotal || 0))})
                      </p>
                    </div>

                    {cashReported !== '' && (
                      <div className="flex justify-between items-center p-3 rounded-xl border bg-white text-xs shadow-sm">
                        <span className="font-medium text-[#46371c]">Diferencia de Efectivo:</span>
                        <Badge className={`font-bold text-xs py-1 px-3 ${parseFloat(cashReported) - (currentSession.opening_base_cash + (currentSales.cash_income || 0) - parseFloat(expensesTotal || 0)) === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {formatPrice(parseFloat(cashReported) - (currentSession.opening_base_cash + (currentSales.cash_income || 0) - parseFloat(expensesTotal || 0)))}
                        </Badge>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[#e5dfd3]/60 pt-3 text-xs">
                      <span>Abierto por:</span>
                      <span className="font-medium text-[#46371c]">{currentSession.opened_by?.name || currentSession.opened_by?.email}</span>
                    </div>
                  </div>

                  <div className="border-t border-[#e5dfd3] pt-4 text-center">
                    <p className="font-body text-xs text-[#75664d]">El arqueo y cuadre final se registrará automáticamente en el historial al cerrar caja.</p>
                  </div>
                </Card>
              </div>
            </div>
          ) : (
            /* ==================== REGISTER IS CLOSED ==================== */
            <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl max-w-4xl mx-auto">
              <div className="flex items-center gap-3 border-b border-[#e5dfd3]/60 pb-4 mb-6">
                <Bookmark className="w-5 h-5 text-[#a0d5ba]" />
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#46371c]">Apertura de Caja Diario</h3>
                  <p className="font-body text-xs text-[#75664d]">Ingresa los valores base y el inventario inicial físico para iniciar las ventas de hoy.</p>
                </div>
              </div>

              <form onSubmit={handleOpenRegister} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block">Base Inicial de Caja (Efectivo) *</label>
                    <input
                      type="number"
                      value={openBaseCash}
                      onChange={e => setOpenBaseCash(e.target.value)}
                      required
                      className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block">Base Inicial para Gastos *</label>
                    <input
                      type="number"
                      value={expensesBase}
                      onChange={e => setExpensesBase(e.target.value)}
                      required
                      className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                </div>

                {/* Initial Inventory Selection */}
                <div>
                  <h4 className="font-heading text-sm font-semibold text-[#46371c] mb-3">Inventario Inicial de Tortas en Vitrina</h4>
                  <div className="overflow-x-auto border border-[#e5dfd3]/60 rounded-xl">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#f4f1ea]">
                          <TableHead className="font-body text-xs font-semibold text-[#46371c]">Torta</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center w-28">Peq (Personal)</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center w-28">Mediana</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center w-28">Grande</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map(p => (
                          <TableRow key={p.id}>
                            <TableCell className="font-body text-xs font-semibold text-[#46371c]">{p.name}</TableCell>
                            <TableCell className="text-center">
                              <input type="number" min={0} value={initialInventory[p.id]?.small ?? 0} onChange={e => handleQtyChange(initialInventory, setInitialInventory, p.id, 'small', e.target.value)} className="w-16 h-8 text-center border border-[#e5dfd3] rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba]" />
                            </TableCell>
                            <TableCell className="text-center">
                              <input type="number" min={0} value={initialInventory[p.id]?.medium ?? 0} onChange={e => handleQtyChange(initialInventory, setInitialInventory, p.id, 'medium', e.target.value)} className="w-16 h-8 text-center border border-[#e5dfd3] rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba]" />
                            </TableCell>
                            <TableCell className="text-center">
                              <input type="number" min={0} value={initialInventory[p.id]?.large ?? 0} onChange={e => handleQtyChange(initialInventory, setInitialInventory, p.id, 'large', e.target.value)} className="w-16 h-8 text-center border border-[#e5dfd3] rounded-lg font-body text-xs focus:outline-none focus:border-[#a0d5ba]" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <Button type="submit" className="w-full bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl h-11">
                  Abrir Caja e Iniciar Turno
                </Button>
              </form>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            {loadingHistory ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : sessionsHistory.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No se han registrado sesiones de caja anteriormente</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f4f1ea]">
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Fecha</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Sede</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Base</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Gastos</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Ventas Calc</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Reportado</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c]">Diferencia</TableHead>
                    <TableHead className="font-body text-xs font-semibold text-[#46371c] text-right">Detalles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionsHistory.map(session => {
                    const isDiff = session.difference !== 0;
                    const diffColor = session.difference < 0 ? 'text-red-600 bg-red-50' : 'text-green-700 bg-green-50';
                    return (
                      <TableRow key={session.id}>
                        <TableCell className="font-body text-sm text-[#46371c]">{session.date}</TableCell>
                        <TableCell className="font-body text-sm text-[#75664d]">{session.store_name}</TableCell>
                        <TableCell className="font-body text-sm text-[#75664d]">{formatPrice(session.opening_base_cash)}</TableCell>
                        <TableCell className="font-body text-sm text-[#75664d]">{formatPrice(session.expenses_total || 0)}</TableCell>
                        <TableCell className="font-body text-sm text-[#75664d]">{formatPrice(session.calculated_income || 0)}</TableCell>
                        <TableCell className="font-body text-sm text-[#75664d]">{formatPrice(session.cash_reported || 0)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${isDiff ? diffColor : 'bg-gray-100 text-gray-800'}`}>
                            {formatPrice(session.difference || 0)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button onClick={() => openHistoryDetail(session)} size="sm" variant="outline" className="h-8 rounded-lg text-xs border-[#e5dfd3]">
                              Ver Detalle
                            </Button>
                            {currentUser?.role === 'admin' && (
                              <Button
                                onClick={() => handleDeleteSession(session.id)}
                                size="sm"
                                variant="destructive"
                                className="h-8 w-8 p-0 rounded-lg flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"
                                title="Eliminar cuadre de caja"
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* History Session Detail Modal */}
      {selectedHistorySession && (
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="max-w-4xl bg-white border border-[#e5dfd3] rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#46371c]">
                Detalle Cuadre de Caja - {selectedHistorySession.store_name} ({selectedHistorySession.date})
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-body text-sm border-b border-[#e5dfd3]/60 pb-4">
                <div>
                  <span className="text-[#75664d] text-xs">Abierto por:</span>
                  <p className="font-semibold text-[#46371c]">{selectedHistorySession.opened_by?.name || selectedHistorySession.opened_by?.email}</p>
                </div>
                {selectedHistorySession.closed_by && (
                  <div>
                    <span className="text-[#75664d] text-xs">Cerrado por:</span>
                    <p className="font-semibold text-[#46371c]">{selectedHistorySession.closed_by?.name || selectedHistorySession.closed_by?.email}</p>
                  </div>
                )}
                <div>
                  <span className="text-[#75664d] text-xs">Apertura:</span>
                  <p className="font-medium text-[#46371c]">{new Date(selectedHistorySession.created_at).toLocaleTimeString()}</p>
                </div>
                {selectedHistorySession.closed_at && (
                  <div>
                    <span className="text-[#75664d] text-xs">Cierre:</span>
                    <p className="font-medium text-[#46371c]">{new Date(selectedHistorySession.closed_at).toLocaleTimeString()}</p>
                  </div>
                )}
              </div>

              {/* Payment Methods Breakdown for Historical Session */}
              {selectedHistorySession.payments_by_method && Object.keys(selectedHistorySession.payments_by_method).length > 0 && (
                <div>
                  <h4 className="font-heading text-sm font-bold text-[#46371c] mb-3">Discriminación por Forma de Pago</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 font-body text-xs">
                    {Object.entries(selectedHistorySession.payments_by_method).map(([methodKey, amt]) => {
                      const info = PAYMENT_METHOD_INFO[methodKey] || { label: methodKey.toUpperCase(), icon: '💰', badgeClass: 'bg-gray-100 text-[#46371c]' };
                      return (
                        <div key={methodKey} className={`p-3 rounded-xl border flex flex-col justify-between ${amt > 0 ? (methodKey === 'efectivo' ? 'bg-green-50/70 border-green-200' : 'bg-[#fcfbf9] border-[#e5dfd3]/70') : 'opacity-60 bg-gray-50 border-gray-200'}`}>
                          <span className="text-[#75664d] flex items-center gap-1.5 font-medium">
                            <span>{info.icon}</span> {info.label}
                            {methodKey === 'efectivo' && <span className="text-[10px] text-green-700 font-bold">(Caja)</span>}
                          </span>
                          <span className={`font-bold text-sm mt-1.5 ${amt > 0 && methodKey === 'efectivo' ? 'text-green-800' : 'text-[#46371c]'}`}>
                            {formatPrice(amt)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cash Reconciliation Summary */}
              <div>
                <h4 className="font-heading text-sm font-bold text-[#46371c] mb-3">Conciliación de Efectivo</h4>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 font-body text-sm">
                  <div className="p-3 bg-[#fcfbf9] border border-[#e5dfd3]/60 rounded-xl">
                    <span className="text-xs text-[#75664d]">Base Caja</span>
                    <p className="font-bold text-[#46371c] text-base mt-1">{formatPrice(selectedHistorySession.opening_base_cash)}</p>
                  </div>
                  <div className="p-3 bg-[#fcfbf9] border border-[#e5dfd3]/60 rounded-xl">
                    <span className="text-xs text-[#75664d]">Base Gastos</span>
                    <p className="font-bold text-[#46371c] text-base mt-1">{formatPrice(selectedHistorySession.expenses_base)}</p>
                  </div>
                  <div className="p-3 bg-[#fcfbf9] border border-[#e5dfd3]/60 rounded-xl">
                    <span className="text-xs text-[#75664d]">Gastos Reales</span>
                    <p className="font-bold text-red-500 text-base mt-1">{formatPrice(selectedHistorySession.expenses_total || 0)}</p>
                  </div>
                  <div className="p-3 bg-[#fcfbf9] border border-[#e5dfd3]/60 rounded-xl">
                    <span className="text-xs text-[#75664d]">Total Ventas</span>
                    <p className="font-bold text-[#46371c] text-base mt-1">{formatPrice(selectedHistorySession.calculated_income || 0)}</p>
                  </div>
                  <div className="p-3 bg-green-50/70 border border-green-200 rounded-xl">
                    <span className="text-xs text-green-800 font-medium">Ventas Efectivo</span>
                    <p className="font-bold text-green-800 text-base mt-1">
                      {formatPrice(selectedHistorySession.cash_income !== undefined ? selectedHistorySession.cash_income : (selectedHistorySession.calculated_income || 0))}
                    </p>
                  </div>
                  <div className="p-3 bg-[#eef6f2] border border-[#a0d5ba]/40 rounded-xl col-span-2 md:col-span-1">
                    <span className="text-xs text-[#75664d]">Efectivo Reportado</span>
                    <p className="font-bold text-[#46371c] text-base mt-1">{formatPrice(selectedHistorySession.cash_reported || 0)}</p>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-gray-50 rounded-xl flex justify-between items-center font-body text-sm border border-[#e5dfd3]/60">
                  <span className="text-[#46371c] font-medium">Diferencia de Efectivo (Esperado vs Reportado):</span>
                  <Badge className={`text-sm py-1 px-3 ${selectedHistorySession.difference === 0 ? 'bg-gray-100 text-gray-800' : selectedHistorySession.difference < 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {formatPrice(selectedHistorySession.difference || 0)}
                  </Badge>
                </div>
              </div>

              {/* Inventory Tracking Details */}
              {selectedHistorySession.inventory_tracks?.length > 0 && (
                <div>
                  <h4 className="font-heading text-sm font-bold text-[#46371c] mb-3">Movimiento e Inventario de Tortas (Cantidades)</h4>
                  <div className="overflow-x-auto border border-[#e5dfd3]/60 rounded-xl">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#f4f1ea]">
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] row-span-2">Producto</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>Inv. Inicial</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>Producción</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>Pedidos Sist.</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>Inv. Final</TableHead>
                          <TableHead className="font-body text-xs font-semibold text-[#46371c] text-center" colSpan={3}>Por Facturar (Ajuste)</TableHead>
                        </TableRow>
                        <TableRow className="bg-[#f4f1ea]/40 border-t border-[#e5dfd3]/40">
                          <TableHead></TableHead>
                          {/* Initial */}
                          <TableHead className="text-center text-[10px] text-[#75664d]">P</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">M</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">G</TableHead>
                          {/* Production */}
                          <TableHead className="text-center text-[10px] text-[#75664d]">P</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">M</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">G</TableHead>
                          {/* Orders */}
                          <TableHead className="text-center text-[10px] text-[#75664d]">P</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">M</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">G</TableHead>
                          {/* Final */}
                          <TableHead className="text-center text-[10px] text-[#75664d]">P</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">M</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">G</TableHead>
                          {/* To Be Invoiced */}
                          <TableHead className="text-center text-[10px] text-[#75664d]">P</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">M</TableHead><TableHead className="text-center text-[10px] text-[#75664d]">G</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedHistorySession.inventory_tracks.map(track => (
                          <TableRow key={track.productId}>
                            <TableCell className="font-body text-xs font-semibold text-[#46371c]">{track.productName}</TableCell>
                            {/* Initial */}
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.initial?.small || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.initial?.medium || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.initial?.large || 0}</TableCell>
                            {/* Production */}
                            <TableCell className="text-center font-body text-xs text-green-600">{track.production?.small || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-green-600">{track.production?.medium || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-green-600">{track.production?.large || 0}</TableCell>
                            {/* Orders */}
                            <TableCell className="text-center font-body text-xs text-[#46371c]">{track.orders?.small || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#46371c]">{track.orders?.medium || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#46371c]">{track.orders?.large || 0}</TableCell>
                            {/* Final */}
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.final?.small || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.final?.medium || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs text-[#75664d]">{track.final?.large || 0}</TableCell>
                            {/* To Be Invoiced */}
                            <TableCell className="text-center font-body text-xs font-semibold text-[#46371c]">{track.to_be_invoiced?.small || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs font-semibold text-[#46371c]">{track.to_be_invoiced?.medium || 0}</TableCell>
                            <TableCell className="text-center font-body text-xs font-semibold text-[#46371c]">{track.to_be_invoiced?.large || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => setIsDetailOpen(false)} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl">
                Cerrar Ventana
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
